import React, { useCallback, useMemo } from 'react'
import type { FC } from 'react'
import { actions as uiActions, selectors as uiSelectors } from '@store/ui'
import { selectors as adminSelectors } from '@store/admin'
import { selectors as userSelectors } from '@store/user'
import routes from '@pages/index'
import ConnectedHeading from '@containers/ConnectedHeading'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import useUser from '@hooks/useUser'
import { getLanguage } from '@utils/language'
import { createHelpSectionItems } from '../utils/createHelpSectionItems'
import { createUserMenuItems } from '../utils/createUserMenuItems'
import { createWarningItems } from '../utils/createWarningItems'

type Props = {
  setIsLeftMenuOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

const Heading: FC<Props> = ({ setIsLeftMenuOpen }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { isAdmin } = useUser()
  const loginLink = isAdmin ? routes.adminLogin.route.toUrl() : routes.userLogin.route.toUrl()
  const homeLink = isAdmin ? routes.adminHome.route.toUrl() : routes.userHome.route.toUrl()

  const handleSettingsNavigation = useCallback(() => {
    dispatch(uiActions.setCameToSettingsFromPage(location.pathname))
    history.push(isAdmin ? routes.adminEditUser.route.toUrl() : routes.userEditUser.route.toUrl())
  }, [dispatch, history, isAdmin, location.pathname])

  const { userData } = useSelector(userSelectors.user)
  const { adminData } = useSelector(adminSelectors.admin)
  const loggedAccount = isAdmin ? adminData : userData

  const activeLanguage = getLanguage()

  const helpSectionItems = useMemo(() => createHelpSectionItems({ activeLanguage }), [activeLanguage])
  const userMenuItems = useMemo(
    () => createUserMenuItems(dispatch, handleSettingsNavigation, isAdmin),
    [dispatch, handleSettingsNavigation, isAdmin],
  )

  const incorrectExpeditions = useSelector(uiSelectors.incorrectExpeditions)
  const reservationsOnExpedited = useSelector(uiSelectors.reservationsOnExpedited)
  const waitingForReporter = useSelector(uiSelectors.waitingForReporter)
  const waitingForResolver = useSelector(uiSelectors.waitingForResolver)
  const internalStockChanges = useSelector(uiSelectors.internalStockChanges)
  const warningItems = useMemo(
    () =>
      createWarningItems(
        incorrectExpeditions,
        reservationsOnExpedited,
        waitingForReporter,
        waitingForResolver,
        internalStockChanges,
        isAdmin,
      ),
    [incorrectExpeditions, reservationsOnExpedited, waitingForReporter, waitingForResolver, internalStockChanges, isAdmin],
  )

  return (
    <ConnectedHeading
      loggedAccount={loggedAccount}
      loginLink={loginLink}
      homeLink={homeLink}
      userMenuItems={userMenuItems}
      helpSectionItems={helpSectionItems}
      warningItems={warningItems}
      setIsLeftMenuOpen={setIsLeftMenuOpen}
    />
  )
}

export default Heading
